import { Comment, getRepoSpaceName } from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import * as React from 'react'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useUserData } from '~/hooks-data/use-user-data'
import { useTimeDiff } from '~/hooks/use-time-diff'
import {
  DotsHorizontalIcon,
  FaceIcon,
  PaperPlaneIcon,
} from '@radix-ui/react-icons'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserStore } from '~/app/_store/user-store'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { useToggleState } from '~/hooks/use-toggle-state'
import { Editor, JSONContent } from '@tiptap/core'
import { ActionsMenuComment } from './commands'
import { getActionsForComment } from './commands/get-comment-actions'
import { CommentReactions } from './reactions'
import { CommentFormFooter, useIsEditorEmpty } from './editor/footer'
import { EmojiPicker } from './emoji-picker'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { makeFullNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import { FeedbackCommentEditor } from './editor'
import { Toolbar, ToolbarButton } from '@radix-ui/react-toolbar'
import { SetRequired } from 'type-fest'
import { useCurrentEditor } from '@tiptap/react'
import { invariant } from '@epic-web/invariant'
import { isReallyEmptyJson } from '@basehub/tiptap-api-utils'
import { toast } from 'sonner'
import { formatToSpecific } from '~/lib/time/utils'
import { useCommentDraftStorage } from './editor/use-comment-draft-storage'
import { getId } from '@basehub/utils/id/index'
import { SYSTEM_AUTHORS, productionAssetsURL } from '@basehub/shared-constants'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'

// #region CommentWrapper
/* -------------------------------------------------------------------------- */
/*                                   Wrapper                                  */
/* -------------------------------------------------------------------------- */

export const CommentWrapper = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode
    comment: Comment
    extraActions?: React.ReactNode
    avatarBorder?: boolean
    onEdit?: () => void
    className?: string
    enabledActions?: boolean
    isHovered?: boolean
  }
>(
  (
    {
      isHovered,
      children,
      comment,
      avatarBorder,
      extraActions,
      onEdit,
      className,
      enabledActions = true,
      ...props
    },
    ref
  ) => {
    const data = useSelectedBranchOrCommitQuery().data
    const { repo, team } = useSelectedTeamAndRepo().data ?? {}
    const user = useUserData(comment.authorId, {
      ref: data,
      spaceID: team?.id && repo?.id ? getRepoSpaceName(team.id, repo.id) : null,
    }).data

    const timeDiff = useTimeDiff(new Date(comment.createdAt))

    return (
      <div
        key={comment.id}
        id={`comment-wrapper-${comment.id}`}
        className={clsx('flex gap-2 group', className)}
        ref={ref}
        {...props}
      >
        <BaseHubAvatar
          withBorder={avatarBorder}
          size="small"
          id={comment.authorId}
          name={user?.username ?? user?.id ?? ''}
          className="sticky top-1"
          forceSrc={
            // force src for BaseHub agents
            user?.imageUrl?.includes(productionAssetsURL)
              ? user.imageUrl
              : undefined
          }
        />
        <div className="flex flex-col gap-0.5 flex-1 overflow-hidden">
          <div className="flex gap-1 items-center justify-between overflow-hidden">
            <p className="flex gap-1.5 items-center text-sm text-dark-bright flex-1 min-h-[22px] overflow-hidden">
              <span
                className={clsx(
                  'font-medium truncate',
                  comment.authorId === SYSTEM_AUTHORS.START.id && 'uppercase'
                )}
              >
                {user?.username ?? user?.id ?? ''}{' '}
              </span>
              <SimpleTooltip
                content={formatToSpecific(new Date(comment.createdAt))}
              >
                <span className="text-dark-faint whitespace-nowrap">
                  {timeDiff}
                </span>
              </SimpleTooltip>
            </p>
            <CommentActions
              isHovered={isHovered}
              comment={comment}
              onEdit={onEdit}
              enableActions={enabledActions}
            >
              {extraActions}
            </CommentActions>
          </div>
          <div className="flex flex-col gap-0.5 flex-1">{children}</div>
        </div>
      </div>
    )
  }
)

// #region CommentBox
/* -------------------------------------------------------------------------- */
/*                      Box (wrapper of messages)                             */
/* -------------------------------------------------------------------------- */

export const CommentThreadBox = React.forwardRef<
  HTMLDivElement,
  {
    threadId: string
    children?: React.ReactNode
    className?: string
  }
>(({ threadId, children, className }, ref) => {
  return (
    <div
      className={clsx('flex flex-col gap-5 p-4 pr-3 pb-5', className)}
      id={`comment-box-${threadId}`}
      ref={ref}
    >
      {children}
    </div>
  )
})

// #region CommentActions
/* -------------------------------------------------------------------------- */
/*                                   Actions                                  */
/* -------------------------------------------------------------------------- */

export const CommentActions = React.forwardRef<
  HTMLDivElement,
  {
    rootCommentId?: string
    comment: Comment
    onEdit?: () => void
    children?: React.ReactNode
    enableActions?: boolean
    isHovered?: boolean
  }
>(({ comment, onEdit, children, enableActions, isHovered }, ref) => {
  const currentUser = useUserStore((s) => s.optimisticUser)
  const replicache = useRepoReplicache().data?.replicache
  const emojiPickerToggleState = useToggleState()
  const actionState = useToggleState()

  React.useEffect(() => {
    if (!isHovered && !enableActions) {
      actionState.handleOff()
    }
  }, [isHovered, actionState, enableActions])

  const actions = React.useMemo(() => {
    return getActionsForComment({
      onEdit,
      replicache,
      comment,
      user: currentUser,
      copyLink: () => {
        try {
          const url = new URL(window.location.href, window.location.origin)
          url.searchParams.set(
            'commentId',
            comment.type === 'root' ? comment.id : comment.rootId!
          )
          navigator.clipboard.writeText(encodeURI(url.toString()))
          toast.success('Link copied to clipboard')
        } catch (error) {
          toast.error('Failed to copy link')
        }
      },
    })
  }, [onEdit, replicache, comment, currentUser])

  React.useEffect(() => {
    if (!enableActions && emojiPickerToggleState.isOn) {
      emojiPickerToggleState.handleOff()
    }
  }, [emojiPickerToggleState, enableActions])

  return (
    <Toolbar
      ref={ref}
      className={clsx(
        'flex gap-1 items-end text-dark-faint [&:has(*:focus-visible)>*]:opacity-100 [&:has(*:focus-visible)>*]:visible'
      )}
    >
      {enableActions && (
        <EmojiPicker
          state={emojiPickerToggleState}
          onSelect={async (emoji) => {
            if (!currentUser) return
            await replicache?.mutate.addReaction({
              id: comment.id,
              reaction: emoji.emoji,
              userId: currentUser.id,
            })
          }}
          data-inner-popover
        >
          <ToolbarButton asChild>
            <IconButton
              tabIndex={0}
              className={clsx(
                'hover:text-dark-bright group-hover:opacity-100 opacity-0',
                actionState.isOn && '!opacity-100'
              )}
            >
              <FaceIcon className="size-3.5" />
            </IconButton>
          </ToolbarButton>
        </EmojiPicker>
      )}
      {enableActions && actions && actions.length > 0 && (
        <ActionsMenuComment toggleState={actionState} actions={actions}>
          <ToolbarButton asChild>
            <IconButton
              tabIndex={0}
              className={clsx(
                'hover:text-dark-bright',
                'group-hover:opacity-100 opacity-0 peer-focus-visible:!opacity-100 peer-has-[focus-visible]:opacity-100 focus-visible:opacity-100',
                actionState.isOn && '!opacity-100',
                actionState.isOn && '!text-dark-bright'
              )}
            >
              <DotsHorizontalIcon className="size-3.5" />
            </IconButton>
          </ToolbarButton>
        </ActionsMenuComment>
      )}
      {children}
    </Toolbar>
  )
})

// Thread actions
export const ThreadActions = ({
  actions,
}: {
  actions: {
    icon: React.ReactNode
    label: string
    onClick: () => void
  }[]
}) => {
  return actions.map((action, index) => {
    if (!action.onClick) {
      return (
        <SimpleTooltip content={action.label} key={index}>
          {action.icon}
          <span className="sr-only">{action.label}</span>
        </SimpleTooltip>
      )
    }

    return (
      <SimpleTooltip content={action.label} key={index}>
        <IconButton className="hover:text-dark-bright" onClick={action.onClick}>
          {action.icon}
          <span className="sr-only">{action.label}</span>
        </IconButton>
      </SimpleTooltip>
    )
  })
}

// #region CommentForm
export const CommentForm = ({
  className,
  size = 'small',
  withBorder = false,
  mode = 'multi-line',
  editorClassName,
  onCreate,
  onUpdate,
  onDestroy,
  ...props
}: {
  className?: string
  size?: 'small' | 'medium' | 'none'
  withBorder?: boolean
  mode?: 'multi-line' | 'single-line'
  editorClassName?: string
  onArchive?: () => void
  // and props of FeedbackCommentEditor
} & SetRequired<
  React.ComponentProps<typeof FeedbackCommentEditor>,
  'onSubmit'
>) => {
  const user = useUserStore((s) => s.optimisticUser)
  const editorRef = React.useRef<Editor | null>(null)
  const [isEmpty, setIsEmpty] = React.useState(true)

  return (
    <div
      className={clsx(
        'flex gap-2 pt-0 flex-1 shrink-0',
        size !== 'none' && 'p-4',
        mode === 'single-line' && '!py-0',
        className
      )}
    >
      {size !== 'none' && (
        <div
          className={clsx('flex items-center justify-start shrink-0', {
            'size-6': size === 'small',
            'size-8': size === 'medium',
            'mt-3': mode === 'multi-line',
            'mt-2': mode === 'single-line',
          })}
        >
          <BaseHubAvatar
            size="fill"
            withBorder={withBorder}
            id={user?.id ?? ''}
            name={user ? makeFullNameFromClerkUser(user) : ''}
          />
        </div>
      )}
      <div
        className={clsx(
          'flex relative flex-col bg-dark-shade-hover py-3 px-4 rounded-lg flex-1 max-w-full [word-break:break-word] overflow-x-hidden',
          mode === 'single-line' && '!p-2',
          editorClassName,
          // props.editable !== false && 'cursor-text',
          // eslint-disable-next-line react-compiler/react-compiler
          editorRef.current?.isEditable ? '!cursor-text' : '!cursor-default'
        )}
        onClick={(e) => {
          if (props.editable === false) return
          if (
            e.target instanceof HTMLElement &&
            // ignore buttons, inputs, labels and contenteditable elements
            (['BUTTON', 'INPUT', 'LABEL'].includes(e.target.nodeName) ||
              e.target.closest('[contenteditable]'))
          ) {
            return
          }
          e.currentTarget
            .querySelector<HTMLElement>('[contenteditable]')
            ?.focus()
        }}
      >
        <FeedbackCommentEditor
          mediaSize={{
            height: 86,
            width: 86,
          }}
          setEditor={props.setEditor}
          onCreate={(props) => {
            editorRef.current = props.editor
            onCreate?.(props)
            setIsEmpty(isReallyEmptyJson(props.editor.getJSON()))
          }}
          onUpdate={(props) => {
            editorRef.current = props.editor
            onUpdate?.(props)
            setIsEmpty(isReallyEmptyJson(props.editor.getJSON()))
          }}
          onDestroy={() => {
            editorRef.current = null
            onDestroy?.()
          }}
          alwaysShowPlaceholder
          className={clsx({
            'pr-8': mode === 'single-line',
            'min-h-20': mode === 'multi-line',
          })}
          slotAfter={
            mode === 'multi-line' ? (
              <CommentFormFooter
                handleSubmit={props.onSubmit}
                handleCancel={props.onCancel}
                handleArchive={props.onArchive}
                commentIsEmpty={isEmpty}
                editorKey={props.editorKey}
              />
            ) : (
              <SubmitButton
                editorKey={props.editorKey}
                onSubmit={props.onSubmit}
                className="absolute right-2 bottom-2"
                data-comment-submit-button
              >
                <PaperPlaneIcon className="size-3.5" />
              </SubmitButton>
            )
          }
          {...props}
        />
      </div>
    </div>
  )
}

const SubmitButton = ({
  children,
  onSubmit,
  disabled,
  editorKey,
  ...props
}: {
  children: React.ReactNode
  onSubmit: (state: string, id?: string) => Promise<void>
  editorKey: string | undefined
} & Omit<React.ComponentProps<'button'>, 'onClick' | 'onSubmit'>) => {
  const { editor } = useCurrentEditor()
  const { draft, setDraft } = useCommentDraftStorage(editorKey)
  const isEmptyEditor = useIsEditorEmpty(editor)

  return (
    <IconButton
      intent="primary"
      onClick={() => {
        invariant(editor, 'Editor is not available')
        const state = editor.getJSON()
        if (isEmptyEditor) return
        const id = draft.id
        onSubmit(JSON.stringify(state), id).then(() => {
          setDraft({ id: getId(), value: '' })
          editor.commands.clearContent()
          editor.commands.focus('end')
        })
      }}
      disabled={isEmptyEditor || disabled}
      data-comment-submit-button
      {...props}
    >
      {children}
    </IconButton>
  )
}
// #region utils
/* -------------------------------------------------------------------------- */
/*                                  Utils                                  */
/* -------------------------------------------------------------------------- */

export const safeParseCommentValue = (
  string: string | null
): string | JSONContent => {
  try {
    if (!string) return ''
    return JSON.parse(string) as JSONContent
  } catch {
    return string ?? ''
  }
}

// #region Exports
export { CommentFormFooter, CommentReactions }
